import React from 'react';
import { StyledContainer, StyledCta, StyledCtaArrowButton, StyledHed, styles } from './CategoryRecommendationsCarousel.styles';
import { Content, Header, RecommendationsCarousel } from '@apps/registry/common/components/Catalog/routes/CatalogProductsIndex/components/RecommendationsCarousel';
import { ProductTile } from '@apps/registry/common/components/Catalog/components/ProductTile';
import { SkeletonGroup, SkeletonText } from '@shared/components/Skeleton';
import { Carousel } from '@apps/registry/common/components/Catalog/components/Carousel';
import { ProductTileSkeleton } from '@apps/registry/common/components/Catalog/components/ProductTile/ProductTile';
import { useTranslation } from '@shared/core';
import { useCategoryRecommendationsCarouselController } from '@apps/registry/common/components/Catalog/routes/CatalogProductsIndex/components/CategoryRecommendationsCarousel/CategoryRecommendationsCarousel.controller';
import { withLazyPropOnIntersection } from '@shared/utils/hooks/useLazyLoadImage';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import type { BoxProps } from '@withjoy/joykit';
import { ChevronRight } from '@withjoy/joykit/icons';
import { isCatalogItem } from '../../../../Catalog.types';
import { useCatalogContext } from '../../../../state/Catalog';
import {
  MobileProductQuickAddDrawer,
  MobileProductQuickAddDrawerProvider,
  useMobileProductQuickAddDrawerProvider,
} from '../../../../components/ProductTile/components/MobileProductQuickAddDrawer';

export interface CategoryRecommendationsCarouselProps
  extends Readonly<{
    category: {
      id: string;
      title: string;
      publicUrlPath: string;
    };
    onBrowseCategoryClick?: () => void;
    disableDeferredLoading?: boolean;
    variant?: 'default' | 'spotlight';
    zIndex?: BoxProps['zIndex'];
  }> {}

export const Root = React.forwardRef<HTMLDivElement, CategoryRecommendationsCarouselProps & { isOnScreen: boolean }>((props, ref) => {
  const { category, onBrowseCategoryClick, isOnScreen, variant = 'default', zIndex } = props;
  const { t } = useTranslation('catalogRegistry');
  const categoryRecommendationsCarousel = t('categoryRecommendationsCarousel');
  const { selectedCategory, isProductInRegistry } = useCatalogContext();
  const {
    productList,
    isMobile,
    addCatalogItemToRegistry,
    addCatalogItemLoading,
    showSkeleton,
    deriveTelemetryPageForProduct,
    handleClickForCashDialog,
    hasAnyStorefrontWarning,
    totalProductCount,
  } = useCategoryRecommendationsCarouselController({ isOnScreen, category });
  const { currentProductId: currentQuickAddProductId, currentProductIndex: currentQuickAddProductIndex } = useMobileProductQuickAddDrawerProvider();

  const isVariantSpotlight = variant === 'spotlight';
  return (
    <RecommendationsCarousel
      ref={ref}
      zIndex={zIndex}
    >
      <SkeletonGroup
        isReady={!showSkeleton && isOnScreen}
        placeholder={<CategoryRecommendationsCarousel.Skeleton />}
      >
        <Header enablePeek={true}>
          <Header.Start>
            <StyledHed
              __css={isVariantSpotlight ? styles.hedSpotlight : styles.hed}
              onClick={onBrowseCategoryClick}
            >
              {selectedCategory?.isPopularStoreCategory
                ? categoryRecommendationsCarousel.explorePopularStores({ title: category.title })
                : categoryRecommendationsCarousel.explore({ title: category.title })}
            </StyledHed>
          </Header.Start>
          <Header.End>
            <StyledCta
              __css={isVariantSpotlight ? styles.ctaSpotlight : styles.cta}
              onClick={onBrowseCategoryClick}
              aria-label={isMobile ? categoryRecommendationsCarousel.showAll({ length: totalProductCount }) : undefined}
            >
              {isMobile ? <CtaArrowButton /> : categoryRecommendationsCarousel.showAll({ length: totalProductCount })}
            </StyledCta>
          </Header.End>
        </Header>
        <Content style={{ overflowX: 'clip' }}>
          <Carousel
            totalNumberOfItems={productList.length}
            columns={[2, 3, 4, 5, 6]}
            arrowVisibility={'always'}
            enablePeek={true}
            peekMobileExtraSize={48}
          >
            {productList.filter(isCatalogItem).map((product, index) => {
              return (
                <Carousel.Item key={product.id}>
                  <ProductTile
                    index={index}
                    key={product.id}
                    product={product}
                    componentSource="categoryRec"
                    variant={isVariantSpotlight ? 'spotlightCarouselItem' : 'carouselItem'}
                    addItemLoading={addCatalogItemLoading}
                    deriveTelemetryPageForProduct={deriveTelemetryPageForProduct}
                    addCatalogItem={addCatalogItemToRegistry}
                    categoryType={'category'}
                    handleClickForCashDialog={handleClickForCashDialog}
                    isMobile={isMobile}
                    hasAnyStorefrontWarning={hasAnyStorefrontWarning}
                    shouldOpenPdp={true}
                    isItemAddedToRegistry={isProductInRegistry(product.id)}
                  />
                </Carousel.Item>
              );
            })}
            <MobileProductQuickAddDrawer
              addCatalogItem={addCatalogItemToRegistry}
              addItemLoading={addCatalogItemLoading}
              deriveTelemetryPageForProduct={deriveTelemetryPageForProduct}
              categoryType={'category'}
              handleClickForCashDialog={handleClickForCashDialog}
              catalogItemId={currentQuickAddProductId}
              index={currentQuickAddProductIndex || 0}
              hasAnyStorefrontWarning={hasAnyStorefrontWarning}
              componentSource={'categoryRec'}
              variant={'gridItem'}
              isItemAddedToRegistry={isProductInRegistry(currentQuickAddProductId || '')}
            />
          </Carousel>
        </Content>
      </SkeletonGroup>
    </RecommendationsCarousel>
  );
});

const CtaArrowButton: React.FC = () => {
  return (
    <StyledCtaArrowButton __css={styles.ctaArrowButton}>
      <ChevronRight
        aria-hidden
        title={undefined}
        size={20}
      />
    </StyledCtaArrowButton>
  );
};

const Skeleton = () => {
  const isMobile = useIsMobileScreen();

  return (
    <RecommendationsCarousel>
      <Header enablePeek>
        <Header.Start>
          <SkeletonText
            rows={1}
            skeletonHeight={40}
            width={isMobile ? 200 : 300}
          />
        </Header.Start>
        <Header.End>
          <SkeletonText
            rows={1}
            skeletonHeight={24}
            width={isMobile ? 60 : 90}
          />
        </Header.End>
      </Header>
      <Content style={{ overflowX: 'clip' }}>
        <StyledContainer __css={styles.carouselContainer}>
          <Carousel
            totalNumberOfItems={4}
            columns={[2, 3, 4, 5, 6]}
            arrowVisibility={'hover'}
            placeholder={
              <ProductTileSkeleton
                variant={'carouselItem'}
                isMobile={isMobile}
              />
            }
            showSkeleton={true}
            enablePeek={true}
          />
        </StyledContainer>
      </Content>
    </RecommendationsCarousel>
  );
};

const LazyLoadingCategoryRecommendationsCarousel = withLazyPropOnIntersection(Root, 'isOnScreen');

const DEFERRED_LOADING_PROPS = { onscreen: true, offscreen: false };
const NON_DEFERRED_LOADING_PROPS = { onscreen: true, offscreen: true };

export const CategoryRecommendationsCarousel: React.FC<CategoryRecommendationsCarouselProps> & {
  Skeleton: typeof Skeleton;
} = props => {
  const { disableDeferredLoading } = props;
  return (
    <MobileProductQuickAddDrawerProvider>
      <LazyLoadingCategoryRecommendationsCarousel
        isOnScreen={disableDeferredLoading ? NON_DEFERRED_LOADING_PROPS : DEFERRED_LOADING_PROPS}
        category={props.category}
        onBrowseCategoryClick={props.onBrowseCategoryClick}
        variant={props.variant}
        zIndex={props.zIndex}
      />
    </MobileProductQuickAddDrawerProvider>
  );
};

CategoryRecommendationsCarousel.Skeleton = Skeleton;
